import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

import { CurrencyPipe, DatePipe } from '@angular/common';
import { MarketplaceProgram } from '@app/inputs/inputs-api.model';
import {
  AnyLearningResource,
  LearningResourceViewModel,
} from '@app/inputs/models/learning-resource.view-model';
import { VideoService } from '@app/inputs/services/video.service';
import { MarkdownService } from '@app/markdown/services/markdown.service';
import { SearchFlagsService } from '../../../services/search-flags.service';
import { SearchFacet, SearchFilterConfig } from '@app/search';
import { SearchFilterId } from '@app/search/components/search-filters/search-filters.component';
import { DisplayTypePipe } from '@app/shared/pipes/display-type.pipe';
import { HtmlToPlaintextPipe } from '@app/shared/pipes/htmlToPlaintext.pipe';
import { LDClientSDK } from '@dg/shared-services';
import { LDClient } from 'launchdarkly-js-client-sdk';

import { facetsToFilters, patchFacetConfig } from '@app/search/services/utils';

import { SimpleItemViewModel } from '@app/shared/models/core-view.model';
import { Observable, of } from 'rxjs';
import { AppliedSearchFacet } from '@app/search/components/search-view';

export interface MarketplaceProgramReferenceResponse {
  title: string;
  shortDescription: string;
  smallImageUrl: string;
  programType: string;
  marketplaceInputId: string;
  marketplaceInputSkills?: [
    {
      marketplaceSkillId: number;
      name: string;
    }
  ];
  cost: string;
  provider: {
    name: string;
    marketplaceProviderId: string;
    logoUrl: string;
  };
  url: string;
  programLength: number;
  programLengthUnit: string;
}

interface MarketplaceProgramResponse {
  marketplaceInputId: number;
  reference: MarketplaceProgramReferenceResponse;
  referenceId: number;
  referenceType: string;
  ExternalEnrollmentId?: string;
}

interface MarketplaceSearchResponse {
  facets: SearchFacet[];
  isAcademicsUser: boolean;
  items: MarketplaceProgramResponse[];
  isAcademiesUser: boolean;
  hasMoreItems: boolean;
  pagination: {
    hasMoreItems: boolean;
    total: number;
    totalResults: number;
    page: number;
    pageSize: number;
    numPages: number;
  };
}

export interface MarketplaceFeaturedItems {
  items: LearningResourceViewModel[];
  total: number;
}

export interface FeaturedItemsMatchViewModel
  extends SimpleItemViewModel<LearningResourceViewModel> {}

@Injectable()
export class SearchMarketplaceService {
  public readonly notSpecified = 'Core_NotSpecified';

  constructor(
    private http: NgxHttpClient,
    private translate: TranslateService,
    private htmlToPlaintextPipe: HtmlToPlaintextPipe,
    private markdownService: MarkdownService,
    private sanitizer: DomSanitizer,
    private displayTypePipe: DisplayTypePipe,
    private datePipe: DatePipe,
    private videoService: VideoService,
    private currencyPipe: CurrencyPipe,
    private searchFlagsService: SearchFlagsService,
    @Inject(LDClientSDK) private ldClientSDK: LDClient
  ) {}

  public searchMarketplace(
    term: string,
    facets: AppliedSearchFacet[],
    count: number,
    skip: number,
    page: number
  ) {
    // currently having issues setting the right return type here, will probably need to set return types for
    // searchDataService.loadLearnings and searchService.searchLearnings
    return this.http
      .get<MarketplaceSearchResponse>('/search/MarketplacePrograms', {
        params: {
          terms: term,
          facets: JSON.stringify(facets),
          page,
          count,
          skip,
        },
        forceUriEncoding: true,
      })
      .pipe(
        map((response) => {
          const facets = this.filterFacets(response.facets);
          // Always display all `Type` subitems, otherwise bookmarked searches
          // and page reloads won't display any subitems that aren't pre-selected

          const configs = this.getFilterConfigs(facets);
          const filters = facetsToFilters(
            facets,
            this.notSpecified,
            this.translate.instant(this.notSpecified),
            configs
          );

          const results = response.items.map(
            (result: MarketplaceProgramResponse) => {
              return this.marketplaceResultAsLearningResource(result.reference);
            }
          );
          return {
            ...response,
            term,
            results,
            filters,
            total: response.pagination.total, // temporary, we are getting the API response cleaned up a bit
            hasMoreItems: response.pagination.hasMoreItems,
          };
        })
      );
  }

  public searchFeaturedItems(): Observable<MarketplaceFeaturedItems> {
    return this.http
      .get<MarketplaceProgramResponse[]>('/search/GetMarketplaceFeaturedInputs')
      .pipe(
        map((response) => {
          const items = response.map((result) => {
            return this.marketplaceResultAsLearningResource(result.reference);
          });
          return {
            items,
            total: items.length,
          };
        })
      );
  }

  public marketplaceResultAsLearningResource(
    result: MarketplaceProgramReferenceResponse
  ): LearningResourceViewModel {
    const {
      title,
      shortDescription,
      smallImageUrl,
      programType,
      marketplaceInputId,
      marketplaceInputSkills,
      cost,
      provider: { name: providerName, marketplaceProviderId: providerId },
      url,
      programLength,
      programLengthUnit,
    } = result;

    const tags = marketplaceInputSkills
      ? marketplaceInputSkills.map((skill) => {
          return skill.name;
        })
      : [];

    const learningResourceViewModelInputs: MarketplaceProgram = {
      title,
      summary: shortDescription,
      imageUrl: smallImageUrl,
      resourceType: programType,
      resourceId: marketplaceInputId,
      marketplaceInputId,
      cost: this.currencyPipe.transform(cost),
      providerName: result?.provider?.name,
      providerId: result?.provider?.marketplaceProviderId,
      providerLogo: result?.provider?.logoUrl,
      internalUrl: marketplaceInputId ? marketplaceInputId.toString() : '',
      tags: tags.join(', '),
      url,
      programLength,
      programLengthUnit,
    };

    return new LearningResourceViewModel(
      learningResourceViewModelInputs as AnyLearningResource,
      this.displayTypePipe,
      this.htmlToPlaintextPipe,
      this.markdownService,
      this.sanitizer,
      'Search',
      this.translate,
      this.datePipe,
      this.videoService,
      this.ldClientSDK
    );
  }

  private filterFacets(facets: SearchFacet[]): SearchFacet[] {
    // temporary check while the price range filters are still in development
    return facets.reduce((allFacets, facet) => {
      const addFilter = facet?.config?.isRangeFilter
        ? this.searchFlagsService.showMarketplacePriceRangeFilters
        : true;
      if (addFilter) {
        allFacets.push(facet);
      }
      return allFacets;
    }, []);
  }

  private getFilterConfigs(
    facets: SearchFacet[]
  ): Partial<SearchFilterConfig>[] {
    const minCostFacet = facets.find((f) => f.id === SearchFilterId.MinCost);
    const maxCostFacet = facets.find((f) => f.id === SearchFilterId.MaxCost);

    let configs = patchFacetConfig(SearchFilterId.Type, facets, {
      showEmptyOptions: true,
    });
    if (minCostFacet && maxCostFacet) {
      configs = patchFacetConfig(
        SearchFilterId.MinCost,
        facets,
        { ...minCostFacet.config, showEmptyOptions: true },
        configs
      );
      return patchFacetConfig(
        SearchFilterId.MaxCost,
        facets,
        { ...maxCostFacet.config, showEmptyOptions: true },
        configs
      );
    }
    return configs;
  }
}
